/**
 * V108-6 feedback for the setup screens:
 *   - useSetupToast: the DS Toast with the design's rules — a toast without Undo closes after 6 s, Esc
 *     closes any toast (the DS Toast ignores Esc while a popover / inline form owns it), Ctrl/Cmd+Z runs
 *     Undo, and focus returns to the trigger when a toast closes with focus inside it.
 *   - useAnnouncer: ONE polite live region, always mounted, written only when a result is ready
 *     ("Check now" results, saves). Ticking text ("checked 9 s ago") is never inside a live region (AC-6).
 *   - Ticker: "N s ago" from a timestamp, ticking every second, never above the polling interval.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Toast } from '@aigovops/design-system';

interface ToastState { message: string; onUndo?: () => void; returnFocus?: HTMLElement | null; version: number }

export function useSetupToast(): [ReactNode, (message: string, opts?: { onUndo?: () => void; returnFocus?: HTMLElement | null }) => void] {
  const [t, setT] = useState<ToastState | null>(null);
  const timer = useRef<number | null>(null);
  const close = useCallback(() => { if (timer.current) window.clearTimeout(timer.current); setT(null); }, []);
  const show = useCallback((message: string, opts: { onUndo?: () => void; returnFocus?: HTMLElement | null } = {}) => {
    if (timer.current) window.clearTimeout(timer.current);
    setT((prev) => ({ message, onUndo: opts.onUndo, returnFocus: opts.returnFocus ?? (document.activeElement as HTMLElement | null), version: (prev?.version ?? 0) + 1 }));
    if (!opts.onUndo) timer.current = window.setTimeout(() => setT(null), 6000);
  }, []);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  useEffect(() => {
    if (!t?.onUndo) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !target?.closest?.('input, textarea, select')) {
        e.preventDefault();
        const undo = t.onUndo;
        close();
        undo?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [t, close]);
  const node = t ? (
    <Toast key="setup-toast" message={t.message} version={t.version} returnFocus={t.returnFocus}
      onUndo={t.onUndo ? () => { const u = t.onUndo; close(); u?.(); } : undefined}
      onDismiss={() => { const back = t.returnFocus; close(); if (back && document.activeElement === document.body) back.focus(); }} />
  ) : null;
  return [node, show];
}

/** One always-mounted polite region; `say` clears then writes so a repeat is read again. */
export function useAnnouncer(): [ReactNode, (message: string) => void] {
  const [text, setText] = useState('');
  const say = useCallback((m: string) => { setText(''); window.setTimeout(() => setText(m), 60); }, []);
  return [<div key="announcer" className="su-sr" role="status" aria-live="polite" data-part="announcer">{text}</div>, say];
}

/** "N s ago" since `at` (ms), ticking each second, capped at `cap` (the polling interval). Not a live region. */
export function Ticker({ at, cap = 15 }: { at: number; cap?: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const i = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(i); }, []);
  const s = Math.max(0, Math.min(cap, Math.round((now - at) / 1000)));
  return <span data-ticking="">{s}</span>;
}
